import React, { useContext, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CAlert, CBadge, CButton, CCard, CCardBody, CCardHeader, CCol, CForm,
  CFormInput, CFormSelect, CFormTextarea, CModal, CModalBody, CModalFooter,
  CModalHeader, CModalTitle, CSpinner,
} from '@coreui/react-pro'

import { api, ApiError } from '../api/client'
import { labelForEmail, usePeople } from '../api/people'
import { canEdit, canModifyAny, canModifyRow } from '../auth/capabilities'
import { AuthContext } from '../App'

type Status = 'open' | 'in_progress' | 'in_review' | 'blocked' | 'done' | 'dropped'
type Priority = 'low' | 'normal' | 'high'

interface Task {
  id: number
  title: string
  details: string
  priority: Priority
  status: Status
  created_by: string
  created_at: string
  updated_at: string
  claimed_at: string | null
  comment_count: number
}

interface Comment {
  id: number
  task_id: number
  author: string
  body: string
  created_at: string
}

const STATUS_STYLE: Record<Status, { color: string; label: string }> = {
  open: { color: 'secondary', label: 'Open' },
  in_progress: { color: 'info', label: 'In progress' },
  in_review: { color: 'warning', label: 'In review' },
  blocked: { color: 'danger', label: 'Blocked' },
  done: { color: 'success', label: 'Done' },
  dropped: { color: 'dark', label: 'Dropped' },
}

const PRIORITY_STYLE: Record<Priority, { color: string; label: string }> = {
  high: { color: 'danger', label: 'High' },
  normal: { color: 'secondary', label: 'Normal' },
  low: { color: 'light', label: 'Low' },
}

const ACTIVE: Status[] = ['open', 'blocked', 'in_progress', 'in_review']
const COMPLETED: Status[] = ['done', 'dropped']

/** Backend stores naive UTC; tag it so Date parses it as such. */
function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const PriorityBadge: React.FC<{ p: Priority }> = ({ p }) => (
  <CBadge color={PRIORITY_STYLE[p].color} className={p === 'low' ? 'text-body' : ''}>
    {PRIORITY_STYLE[p].label}
  </CBadge>
)

const Tasks: React.FC = () => {
  const queryClient = useQueryClient()
  const { auth } = useContext(AuthContext)
  const role = auth.user?.role
  const editable = canEdit(role)
  const list = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.get<Task[]>('/v1/tasks'),
  })
  // Author emails display as roster nickname/first name when they match.
  const people = usePeople()
  const who = (email: string | null | undefined) => labelForEmail(people.data, email) || email || ''

  const [showCompleted, setShowCompleted] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Task | null>(null)
  const [modal, setModal] = useState<'closed' | 'add' | 'edit'>('closed')
  const [editing, setEditing] = useState<Task | null>(null)
  const [form, setForm] = useState({
    title: '', details: '', priority: 'normal' as Priority, created_by: '',
  })
  const isAdmin = auth.user?.role === 'admin'
  const users = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<{ id: number; email: string; role: string }[]>('/v1/admin/users'),
    enabled: isAdmin && modal === 'edit',
  })
  const [formError, setFormError] = useState<string | null>(null)
  const [detail, setDetail] = useState<Task | null>(null)
  const [commentBody, setCommentBody] = useState('')

  const comments = useQuery({
    queryKey: ['task-comments', detail?.id],
    queryFn: () => api.get<Comment[]>(`/v1/tasks/${detail!.id}/comments`),
    enabled: !!detail,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tasks'] })

  const save = useMutation({
    mutationFn: (payload: { id: number | null; body: Record<string, unknown> }) =>
      payload.id == null
        ? api.post('/v1/tasks', payload.body)
        : api.put(`/v1/tasks/${payload.id}`, payload.body),
    onSuccess: () => {
      invalidate()
      setModal('closed')
      setFormError(null)
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? err.message : 'Save failed. Try again.')
    },
  })

  const setStatus = useMutation({
    mutationFn: (t: { id: number; status: Status }) =>
      api.put(`/v1/tasks/${t.id}`, { status: t.status }),
    onSuccess: () => {
      invalidate()
      setDetail(null)
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/v1/tasks/${id}`),
    onSuccess: () => {
      invalidate()
      setConfirmDelete(null)
    },
  })

  const addComment = useMutation({
    mutationFn: () => api.post(`/v1/tasks/${detail!.id}/comments`, { body: commentBody }),
    onSuccess: () => {
      setCommentBody('')
      queryClient.invalidateQueries({ queryKey: ['task-comments', detail?.id] })
      invalidate()
    },
  })

  const openAdd = () => {
    setEditing(null)
    setForm({ title: '', details: '', priority: 'normal', created_by: '' })
    setFormError(null)
    setModal('add')
  }

  const openEdit = (t: Task) => {
    setEditing(t)
    setForm({
      title: t.title, details: t.details, priority: t.priority, created_by: t.created_by,
    })
    setFormError(null)
    setModal('edit')
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const body = editing
      ? {
          title: form.title, details: form.details, priority: form.priority,
          // admins may hand the task to another account; '' leaves it alone
          ...(isAdmin ? { created_by: form.created_by } : {}),
        }
      : { title: form.title, details: form.details, priority: form.priority }
    save.mutate({ id: editing?.id ?? null, body })
  }

  const active = useMemo(() => (list.data ?? []).filter((t) => ACTIVE.includes(t.status)), [list.data])
  const completed = useMemo(() => (list.data ?? []).filter((t) => COMPLETED.includes(t.status)), [list.data])

  const renderRow = (t: Task) => (
    <div
      key={t.id}
      className="d-flex flex-wrap align-items-center gap-2 border-bottom py-2"
    >
      <button
        type="button"
        className="btn btn-link p-0 text-start fw-semibold text-decoration-none"
        style={{ minWidth: 0 }}
        onClick={() => { setDetail(t); setCommentBody('') }}
      >
        {t.title}
      </button>
      <CBadge color={STATUS_STYLE[t.status].color}>{STATUS_STYLE[t.status].label}</CBadge>
      {t.priority !== 'normal' && <PriorityBadge p={t.priority} />}
      <span className="small text-body-secondary">
        {who(t.created_by)} · {fmtWhen(t.created_at)}
      </span>
      {t.comment_count > 0 && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 small text-body-secondary"
          onClick={() => { setDetail(t); setCommentBody('') }}
        >
          💬 {t.comment_count}
        </button>
      )}
      <div className="ms-auto d-flex align-items-center gap-2">
        {editable && canModifyRow(role, t.created_by, auth.user?.email) && (
          <>
            <CFormSelect
              size="sm"
              className="w-auto"
              aria-label={`Status for ${t.title}`}
              value={t.status}
              onChange={(e) => setStatus.mutate({ id: t.id, status: e.target.value as Status })}
              disabled={setStatus.isPending}
            >
              {(Object.keys(STATUS_STYLE) as Status[]).map((s) => (
                <option key={s} value={s}>{STATUS_STYLE[s].label}</option>
              ))}
            </CFormSelect>
            <CButton size="sm" color="secondary" variant="outline" onClick={() => openEdit(t)}>
              Edit
            </CButton>
            {canModifyAny(role) && (
              <CButton size="sm" color="danger" variant="outline" onClick={() => setConfirmDelete(t)}>
                Delete
              </CButton>
            )}
          </>
        )}
      </div>
      {t.details && (
        <div className="w-100 small text-body-secondary text-truncate">{t.details}</div>
      )}
    </div>
  )

  return (
    <>
      <CCard className="mb-4">
        <CCardHeader className="d-flex align-items-center flex-wrap gap-2">
          <div>
            <h4 className="mb-0">📋 Task Board</h4>
            <div className="small text-body-secondary">
              The shared to-do queue — add, edit, or grab a task.
            </div>
          </div>
          <div className="ms-auto d-flex align-items-center gap-2">
            {completed.length > 0 && (
              <CButton
                size="sm" color="secondary" variant="outline"
                onClick={() => setShowCompleted((v) => !v)}
              >
                {showCompleted ? 'Hide' : 'Show'} completed ({completed.length})
              </CButton>
            )}
            {editable && <CButton color="primary" onClick={openAdd}>+ New task</CButton>}
          </div>
        </CCardHeader>
        <CCardBody>
          {list.isLoading ? (
            <div className="text-center py-4"><CSpinner /></div>
          ) : list.isError ? (
            <CAlert color="danger">Couldn't load tasks. Refresh the page.</CAlert>
          ) : active.length === 0 && completed.length === 0 ? (
            <div className="text-center text-body-secondary py-4">
              Nothing on the board — add the first task.
            </div>
          ) : (
            <>
              {active.length === 0 && (
                <div className="text-center text-body-secondary py-3">
                  No active tasks. 🎉
                </div>
              )}
              {active.map(renderRow)}
              {showCompleted && (
                <>
                  <div className="small text-uppercase text-body-secondary fw-semibold mt-3 mb-1">
                    Completed
                  </div>
                  {completed.map(renderRow)}
                </>
              )}
            </>
          )}
        </CCardBody>
      </CCard>

      {/* Add / Edit modal */}
      <CModal visible={modal !== 'closed'} onClose={() => setModal('closed')}>
        <CModalHeader>
          <CModalTitle>{modal === 'edit' ? 'Edit task' : 'New task'}</CModalTitle>
        </CModalHeader>
        <CForm onSubmit={onSubmit} className="row g-3 mb-0">
          <CModalBody>
            {formError && <CAlert color="danger" className="mb-3">{formError}</CAlert>}
            <div className="row g-3">
              <CCol md={12}>
                <CFormInput
                  id="task-title" label="Title" type="text" required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </CCol>
              <CCol md={12}>
                <CFormTextarea
                  id="task-details" label="Details" rows={4}
                  value={form.details}
                  onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
                  placeholder="Who's going, budget, links, constraints…"
                />
              </CCol>
              <CCol md={6}>
                <CFormSelect
                  id="task-priority" label="Priority"
                  value={form.priority}
                  onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as Priority }))}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </CFormSelect>
              </CCol>
              {modal === 'edit' && isAdmin && (
                <CCol md={6}>
                  <CFormSelect
                    id="task-creator" label="Creator (admin)"
                    value={form.created_by}
                    onChange={(e) => setForm((f) => ({ ...f, created_by: e.target.value }))}
                  >
                    <option value="">— (stays with current creator)</option>
                    {(users.data ?? []).map((u) => (
                      <option key={u.id} value={u.email}>{u.email}</option>
                    ))}
                  </CFormSelect>
                </CCol>
              )}
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" variant="outline" onClick={() => setModal('closed')}>Cancel</CButton>
            <CButton color="primary" type="submit" disabled={save.isPending}>
              {save.isPending ? <CSpinner size="sm" /> : 'Save'}
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>

      {/* Detail + comments modal */}
      <CModal visible={!!detail} onClose={() => setDetail(null)} size="lg">
        <CModalHeader>
          <CModalTitle>{detail ? `${detail.title}` : ''}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {detail && (
            <>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <CBadge color={STATUS_STYLE[detail.status].color}>
                  {STATUS_STYLE[detail.status].label}
                </CBadge>
                <PriorityBadge p={detail.priority} />
                <span className="small text-body-secondary">
                  added by {who(detail.created_by)} · {fmtWhen(detail.created_at)}
                </span>
              </div>
              {detail.details && (
                <p className="mb-3" style={{ whiteSpace: 'pre-wrap' }}>{detail.details}</p>
              )}

              <div className="small text-uppercase text-body-secondary fw-semibold mb-2">
                💬 Activity
              </div>
              {comments.isLoading ? (
                <div className="text-center py-2"><CSpinner size="sm" /></div>
              ) : (comments.data ?? []).length === 0 ? (
                <div className="text-body-secondary small mb-2">No comments yet.</div>
              ) : (
                (comments.data ?? []).map((c) => (
                  <div key={c.id} className="border rounded p-2 mb-2">
                    <div className="small text-body-secondary">
                      {who(c.author)} · {fmtWhen(c.created_at)}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{c.body}</div>
                  </div>
                ))
              )}
              {editable && (
                <CForm
                  className="d-flex gap-2 align-items-start"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (commentBody.trim()) addComment.mutate()
                  }}
                >
                  <CFormTextarea
                    rows={2} placeholder="Add a comment…"
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                  />
                  <CButton color="primary" type="submit" disabled={addComment.isPending || !commentBody.trim()}>
                    {addComment.isPending ? <CSpinner size="sm" /> : 'Post'}
                  </CButton>
                </CForm>
              )}
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setDetail(null)}>Close</CButton>
        </CModalFooter>
      </CModal>

      {/* Delete confirmation */}
      <CModal visible={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <CModalHeader>
          <CModalTitle>Delete this task?</CModalTitle>
        </CModalHeader>
        <CModalBody>This can't be undone. Are you sure?</CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setConfirmDelete(null)}>
            Cancel
          </CButton>
          <CButton
            color="danger"
            disabled={remove.isPending}
            onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}
          >
            {remove.isPending ? <CSpinner size="sm" /> : 'Delete'}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default Tasks
